'use client';

import { loadDIModules } from '@actr/di/src/hoc/load-di-modules';
import { useInject } from '@actr/di/src/hook/use-inject';
import { aviaSearchFormDirections } from '@actr/specs/src/avia/search-form-directions';
import aviaSearchDIModule from '@actr/stores/src/modules/avia/search';
import { AviaSearchFormStore } from '@actr/stores/src/stores/stores/avia/search-form';
import { Sizes } from '@actr/style/src/specs/theme-v2';
import { Space } from '@actr/ui/src/components/_atoms/space';
import { Container } from '@actr/ui/src/components/container';
import { ContentBlock } from '@actr/ui/src/components/content-block';
import { runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import React, { FC, PropsWithChildren, useEffect } from 'react';

import AirportMap from '@/components/avia/airports/map';
import PopularFlights from '@/components/avia/popular-flights';
import SearchFormHeader from '@/components/avia/search-form-header';
import Breadcrumb from '@/components/common/breadcrumb';
import ServiceAdvantages from '@/components/page-constucts/advantages';
import Faq from '@/components/page-constucts/faq';
import InnerHtmlTextBlock from '@/components/page-constucts/inner-html-text-block';
import SideNavBar from '@/components/page-constucts/side-navigation-bar';
import { SeoAirport } from '@/services/models/avia/airports';
import { getContentBlocks } from '@/utils/get-content-blocks';
import getNavBarItems from '@/utils/nav-bar';
import { dynamicTextIdTemplate } from '@/utils/string/get-dynamic-text-id';

import * as Styled from './style.css';

export type BaseAirportPageProps = {
  data: SeoAirport;
  path: string;
}

const TEXT_CONTENT_PAGE_POSITION = 0;

const BaseAirportPage: FC <BaseAirportPageProps & PropsWithChildren> = observer(({ data, children, path }) => {
  const { search_form_title, breadcrumbs, content, info } = data;
  const { faq, advantages, app_banner, map, popular_airport_flights } = content || {};
  const aviaSearchForm = useInject(AviaSearchFormStore);
  const itemsForNavBar = getNavBarItems(content, [info], TEXT_CONTENT_PAGE_POSITION);

  useEffect(() => {
    const defaultSegments = [{ ...aviaSearchForm.defaultSegment }];

    aviaSearchForm.stopStore();
    runInAction(() => {
      aviaSearchForm.setSegments(defaultSegments);
      aviaSearchForm.setDirection(aviaSearchFormDirections.TWOWAY);
    });
  }, [aviaSearchForm]);

  const crumbFrom = [
    {
      title: 'Дешёвые авиабилеты',
      path: '/',
    }, {
      path: '/airports',
      title: 'Аэропорты',
    }, {
      path: `/airports/${path}`,
      title: breadcrumbs,
    }];

  const contentBlocks = content ? [
    {
      key: 'info',
      condition: !!info?.description,
      render: () => (
        <ContentBlock id={dynamicTextIdTemplate(0)}>
          <InnerHtmlTextBlock
            html={info.description}
          />
        </ContentBlock>
      )
    },
    {
      key: 'map',
      condition: !!map?.list.coordinates.lat,
      render: () => map ? (
        <AirportMap
          title={map.title}
          list={map?.list}
        />
      ) : null
    },
    {
      key: 'popular_airport_flights',
      condition: !!popular_airport_flights?.list?.length,
      render: () => popular_airport_flights ? (
        <PopularFlights
          navigationId="popular_airport_flights"
          title={popular_airport_flights.title}
          flights={popular_airport_flights.list}
        />
      ) : null
    },
    {
      key: 'advantages',
      condition: !!advantages?.list?.enable,
      render: () => (<ServiceAdvantages />)
    },
    {
      key: 'faq',
      condition: !!faq?.list,
      render: () => (
        faq ? (
          <Faq list={faq?.list} title={faq.title} />
        ) : null
      )
    },
  ] : [];

  const blocks = getContentBlocks({
    content, contentBlocks, textContentKey: 'info', textContentOrderNumber: TEXT_CONTENT_PAGE_POSITION
  });

  return (
    <>
      <SearchFormHeader
        title={search_form_title || ''}
      />
      <Container>
        <Breadcrumb crumb={crumbFrom} dynamic={false} />
        <Space size={Sizes.M} />
        <Container className={Styled.ColumnWrapper}>
          <SideNavBar items={itemsForNavBar} isAppBannerEnable={!!app_banner?.list?.enable} isHideNavBarOnMobile={true} />
          <div className={Styled.MainInfo}>
            {blocks.map(block => (
              <React.Fragment key={block.key}>
                {block.render()}
              </React.Fragment>
            ))}
          </div>
        </Container>
        {children}
      </Container>
    </>
  );
});

export default loadDIModules(BaseAirportPage, aviaSearchDIModule);
